import {useCallback,useEffect,useRef,useState} from "react";
import {Crop,X} from "lucide-react";
import {Modal} from "./beui/Modal";
import {RangeSlider} from "./motion/range-slider";
import "./CropPhotoDialog.css";

const OUT_W=1200,OUT_H=900;

export function CropPhotoDialog({ src, queuedCount, onCancel, onDone }: { src: string; queuedCount: number; onCancel: () => void; onDone: (b: Blob) => void }) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [frameWidth,setFrameWidth] = useState(320);
  const frameHeight = frameWidth * 3 / 4;
  const [pos, setPos] = useState({ x: 0, y: 0 });     // image left/top within frame

  const [busy, setBusy] = useState(false);
  const [error,setError] = useState("");


  useEffect(() => {
    const im = new Image();
    im.onload = () => { imgRef.current = im; setNat({ w: im.naturalWidth, h: im.naturalHeight }); };
    im.onerror = () => setError("Could not read this image. Cancel and choose another photo.");
    im.src = src;
    return () => { im.onload=null; im.onerror=null; };
  }, [src]);

  const baseCover = nat ? Math.max(frameWidth / nat.w, frameHeight / nat.h) : 1;
  const eff = baseCover * zoom;
  const dispW = nat ? nat.w * eff : frameWidth;
  const dispH = nat ? nat.h * eff : frameHeight;

  const clamp = useCallback((x: number, y: number) => {
    const minX = frameWidth - dispW, minY = frameHeight - dispH;
    return { x: Math.min(0, Math.max(minX, x)), y: Math.min(0, Math.max(minY, y)) };
  },[frameWidth,frameHeight,dispW,dispH]);
  // Keep the image covering the frame after zooming or resizing.
  useEffect(() => { setPos((p) => clamp(p.x, p.y)); }, [clamp]);


  const frameRef = useRef<HTMLDivElement>(null);
  useEffect(() => { const frame=frameRef.current; if(!frame)return; const measure=()=>setFrameWidth(frame.clientWidth); measure(); const observer=new ResizeObserver(measure); observer.observe(frame); return()=>observer.disconnect(); },[]);
  const pointers = useRef(new Map<number,{x:number;y:number}>());
  const transform = useRef({zoom,pos});
  transform.current={zoom,pos};
  const commitTransform = useCallback((nextZoom:number,nextPos:{x:number;y:number}) => {
    if(!nat)return;
    const bounded=Math.max(1,Math.min(3,nextZoom));
    const width=nat.w*baseCover*bounded,height=nat.h*baseCover*bounded;
    const position={x:Math.min(0,Math.max(frameWidth-width,nextPos.x)),y:Math.min(0,Math.max(frameHeight-height,nextPos.y))};
    transform.current={zoom:bounded,pos:position};setZoom(bounded);setPos(position);
  },[nat,baseCover,frameWidth,frameHeight]);
  const zoomBetween = useCallback((nextZoom:number,from:{x:number;y:number},to=from) => {
    const current=transform.current,bounded=Math.max(1,Math.min(3,nextZoom)),ratio=bounded/current.zoom;
    commitTransform(bounded,{x:to.x-(from.x-current.pos.x)*ratio,y:to.y-(from.y-current.pos.y)*ratio});
  },[commitTransform]);
  useEffect(()=>{const frame=frameRef.current;if(!frame)return;
    const wheel=(event:WheelEvent)=>{event.preventDefault();const box=frame.getBoundingClientRect();zoomBetween(transform.current.zoom*Math.exp(-event.deltaY*0.002),{x:event.clientX-box.left,y:event.clientY-box.top});};
    frame.addEventListener('wheel',wheel,{passive:false});return()=>frame.removeEventListener('wheel',wheel);
  },[zoomBetween]);
  function onDown(e:React.PointerEvent<HTMLDivElement>){
    e.preventDefault();e.currentTarget.setPointerCapture(e.pointerId);pointers.current.set(e.pointerId,{x:e.clientX,y:e.clientY});
  }
  function onMove(e:React.PointerEvent<HTMLDivElement>){
    const previous=pointers.current.get(e.pointerId);if(!previous)return;
    const before=[...pointers.current.values()];
    pointers.current.set(e.pointerId,{x:e.clientX,y:e.clientY});
    const current=transform.current;
    if(pointers.current.size===2){
      const [a,b]=before,[c,d]=[...pointers.current.values()],box=e.currentTarget.getBoundingClientRect();
      const previousDistance=Math.hypot(a.x-b.x,a.y-b.y),distance=Math.hypot(c.x-d.x,c.y-d.y);
      zoomBetween(current.zoom*distance/Math.max(1,previousDistance),{x:(a.x+b.x)/2-box.left,y:(a.y+b.y)/2-box.top},{x:(c.x+d.x)/2-box.left,y:(c.y+d.y)/2-box.top});
    }else if(pointers.current.size===1){
      commitTransform(current.zoom,{x:current.pos.x+e.clientX-previous.x,y:current.pos.y+e.clientY-previous.y});
    }
  }
  function onUp(e:React.PointerEvent<HTMLDivElement>){pointers.current.delete(e.pointerId);}


  function apply() {
    if (!imgRef.current || !nat) return;
    setBusy(true);
    const sx = -pos.x / eff, sy = -pos.y / eff, sW = frameWidth / eff, sH = frameHeight / eff;
    const canvas = document.createElement("canvas");
    canvas.width = OUT_W; canvas.height = OUT_H;
    try {
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas unavailable");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, OUT_W, OUT_H);
      ctx.drawImage(imgRef.current, sx, sy, sW, sH, 0, 0, OUT_W, OUT_H);
      canvas.toBlob((blob) => { setBusy(false); if (blob) onDone(blob); else setError("Could not crop this image. Please retry."); }, "image/jpeg", 0.85);
    }
    catch { setBusy(false); setError("Could not crop this image. Cancel and choose another photo."); }
  }

  return (
    <Modal className="crop-photo-dialog" label="Crop photo" onClose={onCancel} dismissible={!busy}>
      <div className="bg-white p-5 w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3"><Crop className="w-4 h-4 text-navy" /><h3 className="font-bold text-navy">Crop photo</h3></div>
        <p className="text-xs text-mut mb-3">{queuedCount > 0 ? `${queuedCount} more selected photo${queuedCount === 1 ? "" : "s"} will be cropped after this one. ` : ""}Drag to reposition. Scroll or pinch to zoom, or use the slider. Cropped to 4:3.</p>
        <div ref={frameRef} className="crop-photo-frame mx-auto rounded-lg overflow-hidden bg-[#0b1622] relative touch-none select-none"
          style={{ width: "100%", maxWidth: 320, aspectRatio: "4 / 3" }}
          onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
          {nat && <img src={src} alt="crop" draggable={false} style={{ position: "absolute", left: pos.x, top: pos.y, width: dispW, height: dispH, maxWidth: "none", cursor: "grab" }} />}
          <div className="pointer-events-none absolute inset-0 ring-1 ring-white/40" />
        </div>
        <label className="block text-xs font-semibold text-navy mt-4 mb-1">Zoom</label>
        <RangeSlider min={1} max={3} step={0.01} value={zoom} onValueChange={setZoom} showTicks={false} aria-label="Zoom" formatValueText={value => value.toFixed(2) + " times"} />
        {error && <p role="alert" className="mt-3 text-sm text-brand-red">{error}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onCancel} disabled={busy} className="btn-outline h-9 px-4"><X className="w-4 h-4" />Cancel</button>
          <button type="button" onClick={apply} disabled={busy || !nat} className="btn-red h-9 px-4 disabled:opacity-60">{busy ? "Cropping…" : "Apply crop"}</button>
        </div>
      </div></Modal>
  );
}
